import { create } from "zustand";

import { useAuthStore } from "@/state/auth-store";
import type { ReflectionContext, ReflectionFrequency } from "@/types";

/**
 * Whether a reflection prompt should fire in-app for a given moment.
 *
 * All current cadences (evenings / weekly / custom) deliver their nudge as a
 * scheduled local notification (and email) rather than an in-app modal — see
 * src/lib/reflection-reminder.ts — so no context ever triggers an in-app prompt.
 * The hook is kept so existing `requestReflection(...)` call sites stay valid.
 */
export function shouldPromptReflection(
  _frequency: ReflectionFrequency | undefined,
  _context: ReflectionContext,
): boolean {
  return false;
}

type ReflectionState = {
  /** The context of a reflection waiting to be shown, or null. */
  pending: ReflectionContext | null;
  /** Ask to prompt after a moment; ignored unless the user's cadence allows it. */
  request: (context: ReflectionContext) => void;
  dismiss: () => void;
};

export const useReflectionStore = create<ReflectionState>((set, get) => ({
  pending: null,

  request(context) {
    // Never stack prompts on top of each other.
    if (get().pending) return;
    const frequency = useAuthStore.getState().user?.reflectionFrequency;
    if (shouldPromptReflection(frequency, context)) {
      set({ pending: context });
    }
  },

  dismiss() {
    set({ pending: null });
  },
}));

/** Imperative helper for non-React call sites (stores, handlers). */
export function requestReflection(context: ReflectionContext): void {
  useReflectionStore.getState().request(context);
}
